import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, EmptyState, Skeleton } from '@bbpos/ui';
import { billingService } from '@/services/billing.service';
import type { BillReturn, BillReturnStatus } from '@/services/billing.service';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useReturnUpdates } from '@/hooks/use-return-updates';
import { RETURN_STATUS_FILTERS, returnStatusMeta } from '@/lib/return-status';
import { ReturnStatusPill } from '@/components/features/returns/ReturnTrackingCard';

const PAGE_SIZE = 20;

/**
 * Ops simulation T03 — the cashier's returns with their RINV number and
 * status (a manager sees their locations'). Updates live when a manager
 * approves or rejects, so nobody has to refresh or ask.
 */
export function ReturnsPage() {
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const [status, setStatus] = useState<BillReturnStatus | ''>('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<BillReturn[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const result = await billingService.listReturns({
        status: status || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setRows(result.data);
      setTotalPages(Math.max(1, result.meta?.totalPages ?? 1));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load returns.');
    } finally {
      setIsLoading(false);
    }
  }, [status, page]);

  useEffect(() => { load(); }, [load]);
  useReturnUpdates(() => { load(true); });

  const chooseStatus = (next: BillReturnStatus | '') => {
    setStatus(next);
    setPage(1);
  };

  return (
    <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
      <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-gradient-to-r from-violet-600 to-pink-600 text-white shadow-lg animate-fade-in">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/pos/bills')}
          className="text-white/80 hover:text-white hover:bg-white/10"
          aria-label="Back to bill history"
        >
          ‹ <span className="ml-1 hidden sm:inline">Bills</span>
        </Button>
        <h1 className="text-base font-bold">Returns</h1>
      </header>

      <div className="shrink-0 flex flex-wrap gap-2 px-6 py-3 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        {(['', ...RETURN_STATUS_FILTERS] as Array<BillReturnStatus | ''>).map((s) => {
          const active = s === status;
          return (
            <button
              key={s || 'ALL'}
              type="button"
              onClick={() => chooseStatus(s)}
              aria-pressed={active}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 ${
                active
                  ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white shadow-md'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600'
              }`}
            >
              {s ? returnStatusMeta(s).label : 'All'}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {error && <Alert variant="danger" description={error} dismissible onDismiss={() => setError(null)} />}

        {isLoading ? (
          <Skeleton variant="table-row" count={6} />
        ) : rows.length === 0 ? (
          <EmptyState title="No returns" description="Returns you process or send for approval show up here." />
        ) : (
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md border border-neutral-100 dark:border-neutral-700 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider border-b border-neutral-200 dark:border-neutral-700">
                  <th className="px-4 py-2">Return</th>
                  <th className="px-4 py-2">Invoice</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2">Requested</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/pos/bills/${r.bill_id}`)}
                    className="cursor-pointer border-b border-neutral-100 dark:border-neutral-700 even:bg-neutral-50 dark:even:bg-neutral-900/40 hover:bg-brand-50 dark:hover:bg-neutral-700/50 transition-all duration-200 animate-slide-up"
                  >
                    <td className="px-4 py-2 font-mono font-semibold text-neutral-900 dark:text-white">{r.return_number}</td>
                    <td className="px-4 py-2 text-neutral-700 dark:text-neutral-300">
                      {r.bill_number}
                      {r.customer_name ? <span className="ml-2 text-neutral-400">{r.customer_name}</span> : null}
                    </td>
                    <td className="px-4 py-2">
                      <ReturnStatusPill status={r.status} />
                      {r.status === 'REJECTED' && r.review_note && (
                        <p className="mt-1 text-xs text-danger-700 dark:text-danger-300">{r.review_note}</p>
                      )}
                      {r.execution_error && r.status !== 'COMPLETED' && (
                        <p className="mt-1 text-xs text-danger-700 dark:text-danger-300">{r.execution_error}</p>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-neutral-900 dark:text-white">
                      {formatCurrency(Math.abs(Number(r.amount ?? 0)), currency)}
                    </td>
                    <td className="px-4 py-2 text-neutral-500 dark:text-neutral-400">
                      {formatDateTime(r.created_at)}
                      {r.requested_by_name ? <span className="block text-xs">{r.requested_by_name}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <span className="text-sm text-neutral-500 dark:text-neutral-400">
              Page {page} of {totalPages}
            </span>
            <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
